import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import HeroXray from './HeroXray';
import type { XrayControl } from './HeroXray';
import { computeGeo, zoomTarget } from './heroGeometry';

gsap.registerPlugin(ScrollTrigger);

// Timeline units: zoom 0–0.8, diagnostic hold 0.8–1.8
// Zoom 0–0.8 (short), X-Ray hold 0.8–3.8 (long)
const TOTAL = 3.8;
// Scroll-driven scan runs inside the diagnostic hold
const SCAN_FROM = 0.82;
const SCAN_TO = 3.62;

export default function Hero() {
  const sectionRef = useRef<HTMLElement>(null);
  const mediaRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const introRef = useRef<HTMLDivElement>(null);
  const controlRef = useRef<XrayControl | null>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const media = mediaRef.current;
    if (!section || !media) return undefined;
    const mm = gsap.matchMedia();

    mm.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => {
      controlRef.current?.arm(false);
      const target = () => zoomTarget(computeGeo(section.clientWidth, section.clientHeight));
      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: '+=430%',
          pin: true,
          scrub: 0.9,
          invalidateOnRefresh: true,
        },
        // X-Ray follows the scrubbed timeline, so scrolling back reverses it smoothly.
        onUpdate: () => {
          const t = tl.progress() * TOTAL;
          controlRef.current?.scan((t - SCAN_FROM) / (SCAN_TO - SCAN_FROM));
        },
      });
      tl.to(copyRef.current, { autoAlpha: 0, y: -24, duration: 0.3, ease: 'power1.in' }, 0)
        .to(media, {
          x: () => target().x,
          y: () => target().y,
          scale: () => target().scale,
          duration: 0.8,
          ease: 'power2.inOut',
          onUpdate: () => {
            media.style.setProperty('--inv', String(1 / (Number(gsap.getProperty(media, 'scale')) || 1)));
          },
        }, 0)
        .fromTo(introRef.current, { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: 0.15 }, 0.8)
        .to(introRef.current, { autoAlpha: 0, y: -12, duration: 0.15 }, 3.6)
        .to({}, { duration: 0.05 }, 3.75);

      return () => {
        controlRef.current?.scan(0);
        gsap.set(media, { clearProps: 'transform' });
        media.style.setProperty('--inv', '1');
      };
    });

    mm.add('(max-width: 1023px), (prefers-reduced-motion: reduce)', () => {
      controlRef.current?.arm(true, true);
      return () => controlRef.current?.arm(false);
    });

    return () => mm.revert();
  }, []);

  return (
    <section ref={sectionRef} className="relative h-[100svh] min-h-[640px] max-h-[1000px] w-full overflow-hidden bg-primary-100">
      <HeroXray mediaRef={mediaRef} controlRef={controlRef} />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-black/30" />

      <div ref={copyRef} className="pointer-events-none relative z-10 flex h-full w-full flex-col items-center justify-end px-5 pb-14 md:pb-20 text-center">
        <h1 className="max-w-[14ch] md:max-w-none text-[42px] sm:text-[56px] md:text-[64px] lg:text-[76px] font-medium leading-[1.02] tracking-[-0.03em] text-white">
          Home, Back in Motion.
        </h1>
        <p className="mt-5 max-w-md text-sm md:text-base leading-relaxed text-white/90">
          Expert repairs for the appliances that keep your home running.
        </p>
        <div className="pointer-events-auto mt-8 flex flex-col sm:flex-row items-center gap-2.5">
          <Link to="/book" className="inline-flex h-12 items-center whitespace-nowrap rounded-full bg-white px-7 text-sm text-foreground-950 transition-colors hover:bg-primary-50">
            Book a Repair
          </Link>
          <Link to="/services" className="inline-flex h-12 items-center whitespace-nowrap rounded-full border border-white/30 bg-white/15 px-7 text-sm text-white backdrop-blur-sm transition-colors hover:bg-white/25">
            Explore Services
          </Link>
        </div>
      </div>

      {/* Diagnostic-stage instruction (desktop only), left of the centred washer */}
      <div
        ref={introRef}
        className="pointer-events-none invisible absolute left-10 xl:left-16 top-[40%] z-10 isolate hidden w-[300px] opacity-0 lg:block"
      >
        <div
          aria-hidden="true"
          className="absolute -left-40 -top-40 -z-10 h-[calc(100%+320px)] w-[calc(100%+320px)]"
          style={{ background: 'radial-gradient(ellipse 50% 50% at 45% 50%, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.55) 45%, rgba(255,255,255,0) 100%)' }}
        />
        <p className="text-[11px] tracking-[0.18em] text-[#222222]/70">SAMPLE DIAGNOSIS</p>
        <h2 className="mt-3 text-[34px] xl:text-[40px] font-medium leading-[1.05] tracking-[-0.02em] text-[#222222]">
          See Inside. Spot the Issue.
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-[#555555]">
          Keep scrolling to look inside the washer and spot a sample issue.
        </p>
      </div>
    </section>
  );
}